import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  Search,
  Check,
  AlertTriangle,
  ClipboardCheck,
  ClipboardList,
  Clock,
  LogIn,
  LogOut,
  Users,
} from 'lucide-react';
import { VISIBLE_STATIONS } from '@hcc/types';
import { formatTime } from '../../lib/dateFmt';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  usePeopleList,
  useAppendEvent,
  type PersonApi,
  type PersonStatus,
  type PreflightSummary,
} from '../../hooks/usePeople';
import { LeftEarlyModal } from '../../components/person/LeftEarlyModal';
import { PersonAvatar } from '../../components/person/PersonAvatar';
import { PersonTasksModal } from '../../components/person/PersonTasksModal';
import { PersonPreflightModal } from '../../components/person/PersonPreflightModal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { ApiError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { Pill } from '../../components/ui/Pill';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';

/**
 * Coordinator global view (CLAUDE.md 6.2). One screen showing every
 * attendee's status across all stations, plus active flag count
 * and a quick filter for arrival / on-site / completed.
 *
 * Visible to coordinator / admin / super_admin.
 */
const ALLOWED_ROLES = ['coordinator', 'admin', 'super_admin'];

const FILTERS: Array<{ key: PersonStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'not_arrived', label: 'Not yet' },
  { key: 'on_site', label: 'On site' },
  { key: 'completed', label: 'Completed' },
];

export function CoordinatorGlobal() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');
  const [leavingPerson, setLeavingPerson] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [tasksPerson, setTasksPerson] = useState<PersonApi | null>(null);
  const [preflightPerson, setPreflightPerson] = useState<PersonApi | null>(null);
  const status = filter === 'all' ? undefined : filter;
  const list = usePeopleList(q, undefined, status);

  const summary = useMemo(() => {
    const all = list.data ?? [];
    const arrived = all.filter((p) => p.arrived).length;
    const completed = all.filter((p) => {
      if (!p.arrived || !p.stations) return false;
      return VISIBLE_STATIONS.every((s) => p.stations?.[s]?.complete);
    }).length;
    const leftEarly = all.filter((p) => p.left_early).length;
    const flags = all.reduce((s, p) => s + (p.active_flags ?? 0), 0);
    return { total: all.length, arrived, completed, leftEarly, flags };
  }, [list.data]);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  const allowed = user.roles.some((r) => ALLOWED_ROLES.includes(r));
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Coordinator view is restricted.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <PageHeader
          eyebrowIcon={Activity}
          eyebrow="Coordinator"
          title="Global view"
          description="Every attendee's status across the day. Real-time across all screens; no refresh needed."
        />

        {/* Summary cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <SummaryCard
            label="Total"
            value={summary.total}
            icon={<Users className="h-4 w-4" />}
            tone="slate"
          />
          <SummaryCard
            label="On site"
            value={summary.arrived - summary.leftEarly}
            icon={<Check className="h-4 w-4" />}
            tone="emerald"
          />
          <SummaryCard
            label="Completed"
            value={summary.completed}
            icon={<Check className="h-4 w-4" />}
            tone="indigo"
          />
          <SummaryCard
            label="Left early"
            value={summary.leftEarly}
            icon={<LogOut className="h-4 w-4" />}
            tone={summary.leftEarly > 0 ? 'amber' : 'slate'}
          />
          <SummaryCard
            label="Flags"
            value={summary.flags}
            icon={<AlertTriangle className="h-4 w-4" />}
            tone={summary.flags > 0 ? 'red' : 'slate'}
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div
            role="tablist"
            className="inline-flex rounded-token-sm border border-line/15 bg-surface p-0.5 shadow-token-sm"
          >
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-token-sm px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-accent text-fg-on-accent shadow-token-sm'
                    : 'text-fg-muted hover:bg-surface-2'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative grow">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email..."
              className="block h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface pl-9 pr-3 text-sm text-fg shadow-token-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-dim">
                <tr>
                  <th className="px-4 py-2.5">Attendee</th>
                  <th className="px-4 py-2.5">Position</th>
                  <th className="px-4 py-2.5">Arrived</th>
                  <th className="px-2 py-2.5">Tasks</th>
                  <th className="px-2 py-2.5">Pre-employment</th>
                  {VISIBLE_STATIONS.map((s) => (
                    <th key={s} className="px-2 py-2.5 text-center">
                      {s}
                    </th>
                  ))}
                  <th className="px-4 py-2.5">Flags</th>
                  <th className="px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  <tr>
                    <td colSpan={VISIBLE_STATIONS.length + 7} className="p-0">
                      <SkeletonList rows={5} />
                    </td>
                  </tr>
                ) : !list.data || list.data.length === 0 ? (
                  <tr>
                    <td colSpan={VISIBLE_STATIONS.length + 7}>
                      <EmptyState
                        icon={<Users className="h-6 w-6" />}
                        title="No attendees"
                        description={
                          q || filter !== 'all'
                            ? 'Nothing matches your search and filter.'
                            : 'Once attendees are imported and arrive, they show up here.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  list.data.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-divider/10 transition-colors hover:bg-surface-2/60"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <PersonAvatar
                            fullName={p.full_name}
                            photoKey={p.current_photo_s3_key}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-fg">{p.full_name}</div>
                            <div className="text-xs text-fg-dim">{p.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-fg-muted">{p.position ?? '-'}</td>
                      <td className="px-4 py-2.5">
                        {p.left_early ? (
                          <Pill
                            tone="warning"
                            icon={<LogOut className="h-3 w-3" />}
                          >
                            Left early
                          </Pill>
                        ) : p.arrived ? (
                          <Pill tone="success" icon={<Check className="h-3 w-3" />}>
                            Yes
                          </Pill>
                        ) : (
                          <Pill tone="neutral" icon={<Clock className="h-3 w-3" />}>
                            Not yet
                          </Pill>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <TasksCell
                          person={p}
                          onOpen={() => setTasksPerson(p)}
                        />
                      </td>
                      <td className="px-2 py-2.5">
                        <PreflightCell
                          person={p}
                          onOpen={() => setPreflightPerson(p)}
                        />
                      </td>
                      {VISIBLE_STATIONS.map((s) => {
                        const entry = p.stations?.[s];
                        const done = entry?.complete ?? false;
                        const eventId = entry?.latest_kit_issued_event_id ?? null;
                        return (
                          <td key={s} className="px-2 py-2.5 text-center">
                            {done ? (
                              eventId ? (
                                <a
                                  href={`/api/admin/kit-receipts/${eventId}/pdf?person_id=${p.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-success-soft/25 text-success-soft-fg transition-colors hover:bg-success-soft/40 focus:outline-none focus:ring-2 focus:ring-success"
                                  aria-label={`View ${s} receipt for ${p.full_name}`}
                                  title={`View ${s} receipt (signature + items signed for)`}
                                >
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                </a>
                              ) : (
                                <span
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-success-soft/25 text-success-soft-fg"
                                  aria-label={`${s} done`}
                                  title={`${s} done`}
                                >
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                </span>
                              )
                            ) : (
                              <span
                                className="inline-block h-3 w-3 rounded-full border border-line-strong/30"
                                aria-label={`${s} pending`}
                              />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2.5">
                        {(p.active_flags ?? 0) > 0 ? (
                          <Pill tone="danger" icon={<AlertTriangle className="h-3 w-3" />}>
                            {p.active_flags}
                          </Pill>
                        ) : (
                          <span className="text-xs text-fg-dim">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {p.left_early ? (
                          <CameBackButton
                            personId={p.id}
                            personName={p.full_name}
                            leftEarlyEventId={p.left_early_event_id ?? null}
                            leftAt={p.left_early_at ?? null}
                            onUndone={() => list.refetch()}
                          />
                        ) : p.arrived ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={<LogOut className="h-3.5 w-3.5" />}
                            onClick={() =>
                              setLeavingPerson({ id: p.id, name: p.full_name })
                            }
                          >
                            Left early
                          </Button>
                        ) : (
                          <span className="text-xs text-fg-dim">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {leavingPerson ? (
        <LeftEarlyModal
          open
          personId={leavingPerson.id}
          personName={leavingPerson.name}
          onClose={() => setLeavingPerson(null)}
          onRecorded={() => list.refetch()}
        />
      ) : null}

      <PersonTasksModal
        open={!!tasksPerson}
        personId={tasksPerson?.id ?? ''}
        personName={tasksPerson?.full_name ?? ''}
        onClose={() => {
          setTasksPerson(null);
          // The modal writes events that change outstanding_tasks; refresh
          // the list so the row count updates without a manual reload.
          list.refetch();
        }}
      />
      <PersonPreflightModal
        open={!!preflightPerson}
        personId={preflightPerson?.id ?? ''}
        personName={preflightPerson?.full_name ?? ''}
        onClose={() => {
          setPreflightPerson(null);
          list.refetch();
        }}
      />
    </main>
  );
}

/**
 * Outstanding-tasks cell. Renders a clickable Pill whose tone scales
 * with how much chasing is needed. Tone choice mirrors the principle
 * "danger = needs intervention, warning = waiting on someone,
 * neutral = nothing outstanding". The whole cell opens the task modal
 * so the coordinator can fix the underlying problem without leaving
 * the table.
 */
function TasksCell({
  person,
  onOpen,
}: {
  person: PersonApi;
  onOpen: () => void;
}) {
  const count = person.outstanding_tasks ?? 0;
  if (count === 0) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title="No tasks outstanding — open to view / add"
        aria-label={`Tasks for ${person.full_name}`}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-fg-dim transition-colors hover:text-fg"
      >
        <ClipboardList className="h-3 w-3" />
        —
      </button>
    );
  }
  // Without a per-status breakdown on the wire we can't distinguish
  // 'changes_requested' from 'pending' at this layer, so the tone
  // stays warning ("someone has to do something"). The drill-in modal
  // reveals the breakdown.
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${count} outstanding task${count === 1 ? '' : 's'} for ${person.full_name}`}
      title={`${count} outstanding — click to view`}
      className="inline-flex"
    >
      <Pill tone="warning" icon={<ClipboardList className="h-3 w-3" />}>
        {count}
      </Pill>
    </button>
  );
}

/**
 * Preflight summary cell. Tone reflects severity:
 *   danger  → any 'missing' (chase right now)
 *   warning → any 'pending' (chase eventually)
 *   success → all 'ok' or 'na'
 *   neutral → field not yet set in metadata
 * Tooltip names the specific fields ("DBS, P45 missing") so the
 * coordinator knows what to chase without opening the modal.
 */
function PreflightCell({
  person,
  onOpen,
}: {
  person: PersonApi;
  onOpen: () => void;
}) {
  const pf = person.preflight;
  if (!pf || (pf.dbs === null && pf.rtw === null && pf.dvla === null && pf.p45 === null)) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title="Pre-employment checklist not set — click to fill in"
        aria-label={`Pre-employment checklist for ${person.full_name}`}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-fg-dim transition-colors hover:text-fg"
      >
        <ClipboardCheck className="h-3 w-3" />
        —
      </button>
    );
  }
  const missing = pf.missing_count;
  const pending = pf.pending_count;
  const fieldLabel = { dbs: 'DBS', rtw: 'RTW', dvla: 'DVLA', p45: 'P45' } as const;
  if (missing > 0) {
    const names = (Object.keys(fieldLabel) as Array<keyof typeof fieldLabel>)
      .filter((k) => pf[k] === 'missing')
      .map((k) => fieldLabel[k]);
    return (
      <button
        type="button"
        onClick={onOpen}
        title={`Missing: ${names.join(', ')} — click to update`}
        aria-label={`Pre-employment checks missing for ${person.full_name}: ${names.join(', ')}`}
        className="inline-flex"
      >
        <Pill tone="danger" icon={<ClipboardCheck className="h-3 w-3" />}>
          {names.join(', ')}
        </Pill>
      </button>
    );
  }
  if (pending > 0) {
    const names = (Object.keys(fieldLabel) as Array<keyof typeof fieldLabel>)
      .filter((k) => pf[k] === 'pending')
      .map((k) => fieldLabel[k]);
    return (
      <button
        type="button"
        onClick={onOpen}
        title={`Pending: ${names.join(', ')}`}
        aria-label={`Pre-employment checks pending for ${person.full_name}: ${names.join(', ')}`}
        className="inline-flex"
      >
        <Pill tone="warning" icon={<ClipboardCheck className="h-3 w-3" />}>
          {names.join(', ')}
        </Pill>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      title="All pre-employment checks ok"
      aria-label={`Pre-employment checklist ok for ${person.full_name}`}
      className="inline-flex"
    >
      <Pill tone="success" icon={<ClipboardCheck className="h-3 w-3" />}>
        OK
      </Pill>
    </button>
  );
}

// Type re-export so the cells above can take preflight inline.
export type _Preflight = PreflightSummary;

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: 'slate' | 'emerald' | 'indigo' | 'red' | 'amber';
}) {
  const tones = {
    slate: 'bg-neutral-soft/15 text-fg-muted',
    emerald: 'bg-success-soft/20 text-success-soft-fg',
    indigo: 'bg-accent/10 text-accent',
    red: 'bg-danger-soft/20 text-danger-soft-fg',
    amber: 'bg-warning-soft/20 text-warning-soft-fg',
  } as const;
  return (
    <div className="rounded-token-lg border border-line/10 bg-surface p-4 shadow-token-sm">
      <div className="flex items-center gap-2">
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-token-sm ${tones[tone]}`}
          aria-hidden
        >
          {icon}
        </span>
        <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
          {label}
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-fg">
        {value}
      </div>
    </div>
  );
}

/**
 * Undo a LeftEarly. Appends a LeftEarlyUndone referencing the
 * still-active LeftEarly event id; the SQL projection then treats
 * the person as on-site again.
 */
function CameBackButton({
  personId,
  personName,
  leftEarlyEventId,
  leftAt,
  onUndone,
}: {
  personId: string;
  personName: string;
  leftEarlyEventId: string | null;
  leftAt: string | null;
  onUndone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const append = useAppendEvent(personId);
  const stamp = leftAt
    ? formatTime(leftAt)
    : '';
  async function go(reason?: string) {
    if (!leftEarlyEventId) {
      toast.error('No left-early record to undo');
      setOpen(false);
      return;
    }
    try {
      await append.mutateAsync({
        event_type: 'LeftEarlyUndone',
        payload: {
          left_early_event_id: leftEarlyEventId,
          ...(reason ? { reason } : {}),
        },
      } as Parameters<typeof append.mutateAsync>[0]);
      toast.success(`${personName} marked back on site`);
      onUndone();
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'failed to undo');
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!leftEarlyEventId}
        className="inline-flex items-center gap-1 rounded-token-sm border border-success/30 bg-success-soft/15 px-2 py-1 text-xs font-medium text-success-soft-fg shadow-token-sm transition-colors hover:bg-success-soft/25 disabled:opacity-50"
        title={`Undo the left-early flag from ${stamp}`}
      >
        <LogIn className="h-3.5 w-3.5" />
        Came back
      </button>
      <ConfirmModal
        open={open}
        tone="warning"
        icon={LogIn}
        title={`Mark ${personName} as back on site?`}
        message={
          <>
            Undoes the "left early" record from{' '}
            <strong className="tabular-nums">{stamp}</strong>. The original
            event stays in the audit trail; the projection treats them as
            on-site again.
          </>
        }
        confirmLabel="Mark as back"
        cancelLabel="Cancel"
        reasonLabel="Note (optional)"
        reasonPlaceholder="e.g. came back to collect tools"
        onConfirm={go}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
